import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { getLang, getProcessEnvironmentValue } from '@gif/shared-global';

// Types
import { GameHubContentResponse } from '../types/Games';
import {
  GetApprovedCountriesResponse,
  PostRefreshProfileResponse,
  HubContentQueryProps,
  RefreshProfileQueryProps,
  SetCountryOfResidenceQueryProps,
  PutSetCountryOfResidenceResponse,
  ArcadeLanguageEnums,
} from '../types/ArcadeApi';

// Store
import { RootState } from '../store';

const appHost = getProcessEnvironmentValue('APP_HOST') as string;
const lang =
  getLang() === 'es' ? ArcadeLanguageEnums.ES : ArcadeLanguageEnums.EN;

// Defines the Cerberus calls for Arcade services expected endpoints
export const arcadeCerberusApi = createApi({
  reducerPath: 'arcadeCerberusApi',
  baseQuery: fetchBaseQuery({
    baseUrl: appHost
      ? appHost
      : 'https://external-gaming-platform.gamingeng-gcp.mlbinfra.com',
    prepareHeaders: (headers) => {
      headers.set('Game-Api-Name', 'cerberus');
      return headers;
    },
  }),
  endpoints: (builder) => ({
    getHubContent: builder.query<GameHubContentResponse, HubContentQueryProps>({
      query: (arg) => {
        const { uid, language } = arg;
        return {
          url: `/api/arcade/hub/v1`,
          params: {
            uid,
            language: language || lang,
          },
        };
      },
    }),
    logGameClick: builder.mutation({
      query: (arg) => {
        const { gameId, uid } = arg;
        return {
          url: `/api/arcade/hub/v1/click?gameId=${gameId}${
            uid ? `&uid=${uid}` : ''
          }`,
          method: 'POST',
        };
      },
    }),
  }),
});

// Defines the Garrison calls for Arcade services expected endpoints
export const arcadeGarrisonApi = createApi({
  reducerPath: 'arcadeGarrisonApi',
  baseQuery: fetchBaseQuery({
    baseUrl: appHost ? appHost : 'https://gaming.gamingeng-gcp.mlbinfra.com',
    prepareHeaders: (headers, { getState }) => {
      const accessToken = (getState() as RootState).user.user?.accessToken
        ?.accessToken;
      if (accessToken) {
        headers.set('Authorization', `Bearer ${accessToken}`);
      }
      headers.set('Game-Api-Name', 'garrison');
      return headers;
    },
  }),
  endpoints: (builder) => ({
    refreshProfile: builder.mutation<
      PostRefreshProfileResponse,
      RefreshProfileQueryProps
    >({
      query: (arg) => {
        const { hardRefresh = false } = arg;
        return {
          url: `/api/arcade/users/v1/refresh?hardRefresh=${hardRefresh}`,
          method: 'POST',
        };
      },
    }),
    getApprovedCountries: builder.query<GetApprovedCountriesResponse, void>({
      query: () => `/api/arcade/countries/v1`,
    }),
    setCountryOfResidence: builder.mutation<
      PutSetCountryOfResidenceResponse,
      SetCountryOfResidenceQueryProps
    >({
      query: (arg) => {
        const { uid, ...body } = arg;
        return {
          url: `/api/arcade/users/v1/${uid}/country`,
          // The original garrison API has this as PUT but MLB beta/prod does not support PUTs
          method: 'POST',
          body,
        };
      },
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints - must lead with "use" and end in "Query"
export const { useGetHubContentQuery, useLogGameClickMutation } =
  arcadeCerberusApi;
export const {
  useGetApprovedCountriesQuery,
  useRefreshProfileMutation,
  useSetCountryOfResidenceMutation,
} = arcadeGarrisonApi;
